import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { getStudentMediaStatsQuery } from 'modules/media/graphql/mediaStats/queries';
import { formatValue, timeString } from '@ts-core/utils/dataTableUtils';

import { useFilteredData } from '@ts-core/hooks/ui';

export default function useStudentMediaStats({ courseId, studentId }) {
  const intl = useIntl();
  const { loading, error, data, refetch } = useQuery(getStudentMediaStatsQuery, {
    variables: { courseId, studentId },
    skip: !courseId || !studentId,
    fetchPolicy: 'network-only',
  });
  const mediaStats = useMemo(() => data?.mediaStats || {}, [data]);

  const { mediaItems: allMediaItems, ...others } = mediaStats;
  const { filteredData: mediaItems, onFilterData: onFilterStats } = useFilteredData({
    list: allMediaItems,
    fields: ['name'],
  });

  const onGetXlsxData = useCallback(
    (sortedData) => {
      const { totalViews, avgProgressPercentage, maxProgressPercentage } = mediaStats;

      const headers = [
        intl.formatMessage({ id: 'field.name.label' }),
        intl.formatMessage({ id: 'subject' }),
        intl.formatMessage({ id: 'mediaStatsTable.duration' }),
        intl.formatMessage({ id: 'mediaStatsTable.totalViews' }),
        intl.formatMessage({ id: 'mediaStatsTable.avgProgress' }),
        intl.formatMessage({ id: 'mediaStatsTable.maxProgress' }),
      ];

      const xlsxData = [headers];

      sortedData.forEach((rowData) => {
        const { _original: mediaStatsRow } = rowData;
        const mediaStatsData = [
          mediaStatsRow.name,
          mediaStatsRow.subject.name,
          timeString(mediaStatsRow.duration),
          mediaStatsRow.totalViews,
          formatValue(mediaStatsRow.avgProgressPercentage, 1),
          formatValue(mediaStatsRow.maxProgressPercentage, 1),
        ];
        xlsxData.push(mediaStatsData);
      });

      xlsxData.push(['', '', '', '', '', '']);
      const footer = [
        '',
        '',
        '',
        totalViews ? `${totalViews}` : '-',
        formatValue(avgProgressPercentage, 1, true, 1),
        formatValue(maxProgressPercentage, 1, true, 1),
      ];

      xlsxData.push(footer);

      return xlsxData;
    },
    [intl, mediaStats]
  );

  return {
    loading,
    error: getErrorMessage({ error }),
    mediaStats: {
      ...others,
      mediaItems,
    },
    onFilterStats,
    onFetchStats: refetch,
    onGetXlsxData,
  };
}
